import { Controller } from "@hotwired/stimulus"

const MAX_TOTAL_BYTES = 20 * 1024 * 1024 // 20 MB

export default class extends Controller {
  static targets = ["zone", "label", "input", "list"]

  connect() {
    this.dt = new DataTransfer()
  }

  over(event) {
    event.preventDefault()
    this.zoneTarget.style.borderColor = "var(--brand)"
    this.zoneTarget.style.background = "rgba(21, 182, 194, 0.06)"
    this.labelTarget.textContent = this.labelTarget.dataset.activeText
  }

  leave() {
    this.resetStyle()
    this.render()
  }

  drop(event) {
    event.preventDefault()
    this.resetStyle()
    if (event.dataTransfer?.files?.length) this.addFiles(event.dataTransfer.files)
    else this.render()
  }

  selected() {
    this.addFiles(this.inputTarget.files)
  }

  // Přidá nové soubory k už vybraným (akumulace přes víc kroků), bez duplicit.
  addFiles(fileList) {
    for (const f of Array.from(fileList)) {
      const dup = Array.from(this.dt.files).some(
        (x) => x.name === f.name && x.size === f.size && x.lastModified === f.lastModified
      )
      if (!dup) this.dt.items.add(f)
    }
    this.syncInput()
  }

  remove(event) {
    event.preventDefault()
    const index = Number(event.currentTarget.dataset.index)
    const kept = Array.from(this.dt.files).filter((_, i) => i !== index)
    this.dt = new DataTransfer()
    kept.forEach((f) => this.dt.items.add(f))
    this.syncInput()
  }

  syncInput() {
    this.inputTarget.files = this.dt.files
    this.render()
  }

  resetStyle() {
    this.zoneTarget.style.borderColor = ""
    this.zoneTarget.style.background = ""
  }

  render() {
    if (!this.hasListTarget || !this.hasInputTarget) return
    const files = Array.from(this.dt.files)

    if (files.length === 0) {
      this.listTarget.innerHTML = ""
      this.labelTarget.textContent = this.labelTarget.dataset.idleText
      return
    }

    const total = files.reduce((sum, f) => sum + f.size, 0)
    const over = total > MAX_TOTAL_BYTES

    this.labelTarget.textContent =
      files.length === 1 ? "1 soubor vybrán" : `${files.length} soubory vybrány`

    const rows = files
      .map(
        (f, i) => `
      <div class="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-on-dark" style="background: rgba(255,255,255,0.04); border: 1px solid var(--dark-line);">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="shrink-0 text-brand" aria-hidden="true">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="truncate flex-1">${this.escape(f.name)}</span>
        <span class="opacity-60 shrink-0">${this.humanSize(f.size)}</span>
        <button type="button" data-action="file-drop#remove" data-index="${i}" aria-label="Odebrat" class="shrink-0 opacity-60 hover:opacity-100 leading-none text-base">&times;</button>
      </div>`
      )
      .join("")

    const totalLine = `
      <div class="text-[12px] ${over ? "text-red-400" : "text-on-dark-muted"} mt-0.5">
        Celkem ${this.humanSize(total)} / max 20 MB${over ? " — překročeno, odeberte něco" : ""}
      </div>`

    this.listTarget.innerHTML = rows + totalLine
  }

  humanSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  escape(str) {
    const el = document.createElement("div")
    el.textContent = str
    return el.innerHTML
  }
}
